/**
 * Admin Contracts Vault — master view of EVERY signed/draft contract in the system.
 * Filter by status (draft/signed) and role (buyer/publisher/affiliate),
 * search by counterparty name/email/company or contract ID,
 * preview/download PDF, resend signed copy.
 */
import React, { useEffect, useState } from "react";
import {
  Loader2, FileText, Lock, CheckCircle2,
  Download, ExternalLink, Send, Search, Plus, Clock, Bell,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IssueContractDialog } from "@/components/admin/IssueContractDialog";
import { RowBin, ClearArea } from "@/components/admin/Purge";

const ROLES   = [{ v: "",            l: "All roles" }, { v: "buyer", l: "Buyers" }, { v: "publisher", l: "Publishers" }, { v: "affiliate", l: "Affiliates" }];
const STATUSES = [{ v: "",           l: "All" }, { v: "signed", l: "Signed" }, { v: "draft", l: "Awaiting" }];

export default function AdminContracts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [role, setRole]     = useState("");
  const [q, setQ]           = useState("");
  const [pdfModal, setPdfModal] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  // iter90o — vault-wide stats (independent of current filters)
  const [vaultStats, setVaultStats] = useState({ total: 0, signed: 0, awaiting: 0, stale_awaiting: 0, recently_issued: 0 });
  // iter90p — Nudge all stale contracts (confirm dialog)
  const [nudgeConfirm, setNudgeConfirm] = useState(false);
  const [nudging, setNudging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        api.get("/admin/contracts", { params: { status: status || undefined, role: role || undefined, q: q || undefined, limit: 200 } }),
        api.get("/admin/contracts/stats"),
      ]);
      setItems(list.data.items || []);
      setVaultStats(stats.data || {});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, role]);

  const preview = async (c) => {
    try {
      const { data } = await api.get(`/contracts/${c.id}/pdf-link`);
      setPdfModal({ c, url: data.url });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const download = async (c) => {
    try {
      const { data } = await api.get(`/contracts/${c.id}/pdf-link`);
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const resend = async (c) => {
    try {
      await api.post(`/admin/contracts/${c.id}/resend`);
      toast.success("Signed contract resent to counterparty.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  // iter90p — bulk re-send to every contract pending sig >7 days
  const nudgeAllStale = async () => {
    setNudging(true);
    try {
      const { data } = await api.post("/admin/contracts/nudge-all-stale");
      const bits = [];
      if (data.nudged)  bits.push(`${data.nudged} nudged`);
      if (data.skipped) bits.push(`${data.skipped} skipped (recently nudged)`);
      if (data.failed)  bits.push(`${data.failed} failed`);
      const msg = bits.length ? bits.join(" · ") : "Nothing to nudge — vault is clean.";
      if (data.failed && !data.nudged) {
        toast.error(msg);
      } else {
        toast.success(msg);
      }
      setNudgeConfirm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not nudge contracts");
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-contracts-page">
      <div>
        <h1 className="text-2xl tracking-[-0.02em] font-semibold text-slate-900">Contracts vault</h1>
        <p className="text-[13.5px] text-slate-500 mt-1">
          Every Buyer, Publisher and Affiliate master agreement. Signed copies are immutable; resend re-emails the original PDF.
        </p>
      </div>

      {/* iter90o — Stale-pending action banner + iter90p "Nudge all" CTA. */}
      {/* Recoloured to navy/slate/brand-blue per brand language — no amber. */}
      {vaultStats.stale_awaiting > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-[#0055FF]/[0.04] px-4 py-3 flex items-center gap-3"
          data-testid="contracts-stale-banner"
        >
          <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0055FF]/10 text-[#0055FF] flex-shrink-0">
            <Clock className="size-4" strokeWidth={2.2} />
          </span>
          <button
            onClick={() => { setStatus("draft"); setRole(""); }}
            className="flex-1 min-w-0 text-left group"
            data-testid="contracts-stale-banner-filter"
          >
            <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight">
              {vaultStats.stale_awaiting} contract{vaultStats.stale_awaiting === 1 ? "" : "s"} pending signature for over 7 days
            </div>
            <div className="text-[12px] text-slate-500">
              These haven&apos;t been signed since issue. Filter to review them &middot; <span className="text-[#0055FF] group-hover:underline">Review &rarr;</span>
            </div>
          </button>
          <Button
            size="sm"
            onClick={() => setNudgeConfirm(true)}
            className="rounded-md h-9 bg-[#0055FF] hover:bg-[#0044CC] text-white gap-1.5 flex-shrink-0"
            data-testid="contracts-nudge-all"
          >
            <Bell className="size-3.5" /> Nudge all
          </Button>
        </div>
      )}

      {/* Vault-wide stat strip — click to filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="Total in vault"
          value={vaultStats.total}
          active={status === "" && role === ""}
          activeLabel="All shown"
          onClick={() => { setStatus(""); setRole(""); }}
          testid="stat-total"
        />
        <Stat
          label="Awaiting signature"
          value={vaultStats.awaiting}
          tone="brand"
          active={status === "draft"}
          onClick={() => setStatus(status === "draft" ? "" : "draft")}
          testid="stat-awaiting"
        />
        <Stat
          label="Signed"
          value={vaultStats.signed}
          tone="emerald"
          active={status === "signed"}
          onClick={() => setStatus(status === "signed" ? "" : "signed")}
          testid="stat-signed"
        />
        <Stat
          label="Issued (7d)"
          value={vaultStats.recently_issued}
          tone="navy"
          testid="stat-recent"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        {/* Filter bar */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Search by name, email, company or ID…"
              className="pl-8 h-9 text-[13px]"
              data-testid="contracts-search"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-[12.5px] bg-white" data-testid="contracts-filter-status">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-[12.5px] bg-white" data-testid="contracts-filter-role">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={load} className="rounded-md h-9">Refresh</Button>
          <div className="flex-1" />
          <ClearArea area="contracts" onDone={load} label="Clear contracts" />
          <Button size="sm" onClick={() => setIssueOpen(true)} className="rounded-md h-9 bg-[#0055FF] hover:bg-[#0044CC] text-white gap-1.5" data-testid="admin-contract-issue">
            <Plus className="size-3.5" /> Issue contract
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="size-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No contracts match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/60 text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-2.5">Contract</th>
                  <th className="text-left px-4 py-2.5">Counterparty</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Issued</th>
                  <th className="text-left px-4 py-2.5">Signed</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => {
                  const signed = c.status === "signed";
                  return (
                    <tr key={c.id} className="hover:bg-slate-50/40">
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-start gap-2.5">
                          <span className={`inline-grid place-items-center size-7 rounded-md flex-shrink-0 mt-0.5 ${signed ? "bg-emerald-500/10 text-emerald-600" : "bg-[#0055FF]/10 text-[#0055FF]"}`}>
                            {signed ? <Lock className="size-3" strokeWidth={2.4} /> : <FileText className="size-3" strokeWidth={1.9} />}
                          </span>
                          <div className="min-w-0">
                            <div className="text-slate-900 font-medium truncate">{c.template_title}</div>
                            <div className="text-[11.5px] text-slate-500 font-mono">{c.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-slate-900">{c.counterparty?.company || c.counterparty?.name || "—"}</div>
                        <div className="text-[11.5px] text-slate-500">{c.counterparty?.email}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-mono font-semibold tracking-[0.14em] uppercase ${
                          signed ? "bg-emerald-500/10 text-emerald-700" : "bg-[#0055FF]/10 text-[#0055FF]"
                        }`}>
                          {signed ? <><CheckCircle2 className="size-2.5" /> Signed</> : <><Clock className="size-2.5" /> Awaiting</>}
                        </span>
                        <div className="text-[10.5px] text-slate-500 font-mono mt-1 uppercase tracking-[0.14em]">{c.role}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-[12px] font-mono text-slate-600 tabular-nums whitespace-nowrap">
                        {c.issued_at ? new Date(c.issued_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3 align-top text-[12px] font-mono text-slate-600 tabular-nums whitespace-nowrap">
                        {c.signed_at ? new Date(c.signed_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          <button onClick={() => preview(c)} className="inline-flex items-center gap-1 text-slate-700 hover:text-[#0055FF] text-[12px] px-2 h-7 rounded hover:bg-slate-50" data-testid={`admin-contract-preview-${c.id}`}>
                            <ExternalLink className="size-3" /> Preview
                          </button>
                          <button onClick={() => download(c)} className="inline-flex items-center gap-1 text-slate-700 hover:text-[#0055FF] text-[12px] px-2 h-7 rounded hover:bg-slate-50" data-testid={`admin-contract-download-${c.id}`}>
                            <Download className="size-3" /> PDF
                          </button>
                          {signed && (
                            <button onClick={() => resend(c)} className="inline-flex items-center gap-1 text-slate-700 hover:text-[#0055FF] text-[12px] px-2 h-7 rounded hover:bg-slate-50" data-testid={`admin-contract-resend-${c.id}`}>
                              <Send className="size-3" /> Resend
                            </button>
                          )}
                          <RowBin area="contracts" id={c.id} name={c.template_title} onDone={load} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pdfModal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl h-[88vh] rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[12px] font-mono text-slate-500">{pdfModal.c.id}</div>
                <div className="text-sm font-semibold text-slate-900">{pdfModal.c.template_title}</div>
              </div>
              <button onClick={() => setPdfModal(null)} className="p-2 rounded-full hover:bg-slate-100">×</button>
            </div>
            <iframe src={pdfModal.url} className="flex-1 w-full bg-slate-50" title="Contract preview" />
          </div>
        </div>
      )}

      <IssueContractDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        onIssued={load}
        onPreview={preview}
      />

      {/* iter90p — Confirm "Nudge all" before broadcasting reminders */}
      {nudgeConfirm && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF]">Confirm action</div>
              <div className="text-lg font-semibold text-slate-900 tracking-tight mt-0.5">Nudge {vaultStats.stale_awaiting} stale contract{vaultStats.stale_awaiting === 1 ? "" : "s"}?</div>
            </div>
            <div className="px-6 py-5 text-[13.5px] text-slate-600 leading-relaxed space-y-2">
              <p>
                This will re-send the original sign-link email (and SMS where a mobile is on file) to every recipient whose contract has been awaiting signature for over 7 days.
              </p>
              <p className="text-slate-500">
                Each recipient is rate-limited to one nudge every 24 hours, so accidental double-clicks are safe.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setNudgeConfirm(false)} className="rounded-md h-10" disabled={nudging} data-testid="nudge-cancel">Cancel</Button>
              <Button onClick={nudgeAllStale} disabled={nudging} className="rounded-md h-10 bg-[#0055FF] hover:bg-[#0044CC] text-white min-w-[140px] gap-1.5" data-testid="nudge-confirm">
                {nudging ? <Loader2 className="size-4 animate-spin" /> : <><Bell className="size-3.5" /> Send nudges</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "slate", active = false, onClick, testid, activeLabel = "Filtered" }) {
  const toneMap = {
    slate:   { text: "text-slate-900",     ring: "ring-slate-300",     bgActive: "bg-slate-50" },
    emerald: { text: "text-emerald-600",   ring: "ring-emerald-300",   bgActive: "bg-emerald-50" },
    // Subtle brand-blue tone — replaces the old amber/yellow scheme. Calm on-brand wash.
    brand:   { text: "text-[#0055FF]",     ring: "ring-[#0055FF]/30",  bgActive: "bg-[#0055FF]/[0.05]" },
    // Deeper navy for the "Issued (7d)" recency callout — distinguishes it from "Awaiting".
    navy:    { text: "text-[#001A4A]",     ring: "ring-slate-400",     bgActive: "bg-slate-50" },
  };
  const t = toneMap[tone] || toneMap.slate;
  const clickable = !!onClick;
  const Component = clickable ? "button" : "div";
  return (
    <Component
      onClick={onClick}
      className={`rounded-xl border bg-white px-5 py-4 text-left transition-all ${
        clickable ? `hover:ring-2 hover:${t.ring} hover:-translate-y-0.5 active:translate-y-0` : ""
      } ${active ? `ring-2 ${t.ring} ${t.bgActive} border-transparent` : "border-slate-200"}`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${t.text}`}>{value}</div>
      {clickable && (
        <div className="text-[10.5px] text-slate-400 mt-1.5 uppercase tracking-[0.14em] font-mono">
          {active ? activeLabel : "Click to filter"}
        </div>
      )}
    </Component>
  );
}

